// /src/store/userManagment/actions.ts

import { User } from '../../dataTypes/userManagment/types';

import {
  FETCH_USERS_REQUEST,
  FETCH_USERS_SUCCESS,
  FETCH_USERS_FAILURE,
  FETCH_USER_BY_ID_REQUEST,
  FETCH_USER_BY_ID_SUCCESS,
  FETCH_USER_BY_ID_FAILURE,
  FETCH_USER_BY_EMAIL_REQUEST,
  FETCH_USER_BY_EMAIL_SUCCESS,
  FETCH_USER_BY_EMAIL_FAILURE,
  UserActionTypes,
  GetUserListParams
} from './types';

import { Dispatch } from 'redux';
import { RootState } from '../generalReducer';
import { store } from '../store'; // Assurez-vous d'importer votre store Redux

import { getUserList, getUserById, getUserByEmail } from '../../backendServices/userManagment/api';

export const fetchUserList = (params: GetUserListParams = {}) => async (dispatch: Dispatch<UserActionTypes>, getState: () => RootState) => {
  const state = getState();
  const lastFetched = state.userListReducer.lastFetchTimestamp;
  const lastFetchParams = state.userListReducer.lastFetchParams;
  const cacheDuration = 10 * 60 * 1000; // 10 minutes en millisecondes

  // Vérifiez si les paramètres de la requête actuelle sont inclus dans les paramètres de la dernière requête
  const isParamsIncluded = (lastFetchParams?.beginningDate && lastFetchParams?.endingDate && params.beginningDate && params.endingDate)
    ? (params.beginningDate >= lastFetchParams.beginningDate && params.endingDate <= lastFetchParams.endingDate)
    : false;

  // Vérifiez si les données sont encore valides et si les paramètres sont inclus
  if (lastFetched && Date.now() - lastFetched < cacheDuration && isParamsIncluded) {
    return; // Retourne sans faire d'appel API si les données sont encore valides
  }

  dispatch({ type: FETCH_USERS_REQUEST, payload: params });

  try {
    const response = await getUserList(params);

    if (response.code === 1) {
      dispatch({
        type: FETCH_USERS_SUCCESS,
        payload: {
          users: response.payload,
          lastFetchTimestamp: Date.now(),
          lastFetchParams:params
        }
      });
    } else {
      dispatch({ type: FETCH_USERS_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_USERS_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const fetchUserById = (userId: number) => async (dispatch: Dispatch<UserActionTypes>, getState: () => RootState) => {
  const state = getState();
  const users = state.userListReducer.users;

  dispatch({ type: FETCH_USER_BY_ID_REQUEST });

  if (users.has(userId)) {
    return; // Les données sont déjà en cache
  }

  try {
    const response = await getUserById(userId);


    if (response.code === 1) {
      dispatch({ type: FETCH_USER_BY_ID_SUCCESS, payload: response.payload });
    } else {
      dispatch({ type: FETCH_USER_BY_ID_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_USER_BY_ID_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};

export const fetchUserByEmail = (email: string) => async (dispatch: Dispatch<UserActionTypes>, getState: () => RootState) => {
  const state = getState();
  const emailToIdMap = state.userListReducer.emailToIdMap;

  dispatch({ type: FETCH_USER_BY_EMAIL_REQUEST });

  if (emailToIdMap.has(email)) {
    const userId = emailToIdMap.get(email);
    if (userId !== undefined) {
      fetchUserById(userId)(dispatch, getState); // Appelle directement la fonction pour récupérer l'utilisateur
    }
    return;
  }

  try {
    const response = await getUserByEmail(email);

    if (response.code === 1) {
      dispatch({ type: FETCH_USER_BY_EMAIL_SUCCESS, payload: response.payload });
    } else {
      dispatch({ type: FETCH_USER_BY_EMAIL_FAILURE, payload: response.comment });
    }
  } catch (error: unknown) {
    dispatch({ type: FETCH_USER_BY_EMAIL_FAILURE, payload: (error as Error).message || 'Une erreur inconnue est survenue.' });
  }
};


export const retrieveUserById = async (userId: number): Promise<User | null> => {

  const { dispatch, getState } = store;
  const state = getState();
  const users = state.userListReducer.users;

  if (users.has(userId)) {
    return users.get(userId) ?? null; // Retourne l'utilisateur si présent dans le cache
  }

  await dispatch(fetchUserById(userId)); // Met à jour le store avec les données de l'utilisateur
  const updatedState = getState();
  return updatedState.userListReducer.users.get(userId) ?? null; // Retourne l'utilisateur après mise à jour du store
};

export const retrieveUserByEmail = async (email: string): Promise<User | null> => {
  const { dispatch, getState } = store;
  const state = getState();
  const emailToIdMap = state.userListReducer.emailToIdMap;

  if (emailToIdMap.has(email)) {
    const userId = emailToIdMap.get(email);
    if (userId !== undefined) {
      return retrieveUserById(userId); // Retourne l'utilisateur si présent dans le cache
    }
  }

  await dispatch(fetchUserByEmail(email)); // Met à jour le store avec les données de l'utilisateur
  const updatedState = getState();
  const userId = updatedState.userListReducer.emailToIdMap.get(email);
  return userId ? updatedState.userListReducer.users.get(userId) ?? null : null; // Retourne l'utilisateur après mise à jour du store
};

export const retrieveUserList = async (params: GetUserListParams = {}): Promise<User[]> => {
  const { dispatch, getState } = store;
  const state = getState();
  const users = state.userListReducer.users;

  if (users.size > 0) {
    return Array.from(users.values()); // Retourne la liste des utilisateurs si présente dans le cache
  }

  await dispatch(fetchUserList(params)); // Met à jour le store avec la liste des utilisateurs
  const updatedState = getState();
  return Array.from(updatedState.userListReducer.users.values()); // Retourne la liste des utilisateurs après mise à jour du store
};

// Fonction pour récupérer le nombre d'utilisateurs ayant un statut donné
export const getUserCountByStatus = (status: string): number => {
  const state = store.getState();
  const users = state.userListReducer.users;

  // Filtrer les utilisateurs par statut et compter le nombre d'utilisateurs correspondants
  return Array.from(users.values()).filter((user: User) => user.status === status).length;
};

// Fonction pour récupérer le nombre d'utilisateurs créés ou fermés entre deux dates avec un statut donné
export const getUserCountByStatusAndDateRange = (
  status: string,
  type: 'Creation' | 'Closure',
  beginningDate: Date,
  endingDate: Date
): number => {
  const state = store.getState();
  const users = state.userListReducer.users;

  // Filtrer les utilisateurs en fonction du statut et de la plage de dates
  return Array.from(users.values()).filter((user: User) => {
    const dateToCheck = type === 'Creation' ? user.creationDate : user.closureDate;
    return user.status === status && dateToCheck && dateToCheck >= beginningDate && dateToCheck <= endingDate;
  }).length;
};
